import { FormControl, FormGroup } from '@angular/forms';
import { nonBlankValidator } from '@app/sharedModule/validators/validators';
import { createTimerFormGroup, TimerFormShape } from '../../components/timer-config/timer.model';
import { DEFAULT_DISCOUNT_LABEL_COLOR, DiscountType } from '../../discount.model';
import {
  CustomCodeFormShape,
  CustomCodeVariableDoc,
  createCustomCodeFormGroup,
} from '../custom-code/custom-code.model';

export type SelectingMethodType = 'toggle' | 'checkbox';
export type DigitalUpsellImageFit = 'contain' | 'cover';
export type DigitalUpsellPricingMethod = 'fixed' | 'percent';
export type DigitalUpsellDiscountApplyTo = 'upsellItem' | 'orderSubtotal';

export interface CardAppearanceFormShape {
  backgroundColor: FormControl<string>;
  textColor: FormControl<string>;
  borderColor: FormControl<string>;
  borderWidth: FormControl<number>;
  cornerRadius: FormControl<number>;
  innerPadding: FormControl<number>;
}

export interface ImageAppearanceFormShape {
  fit: FormControl<DigitalUpsellImageFit>;
  cornerRadius: FormControl<number>;
}

export interface SelectingMethodFormShape {
  type: FormControl<SelectingMethodType>;
  defaultColor: FormControl<string>;
  selectedColor: FormControl<string>;
}

export interface TooltipAppearanceFormShape {
  backgroundColor: FormControl<string>;
  borderColor: FormControl<string>;
  textColor: FormControl<string>;
  borderWidth: FormControl<number>;
  cornerRadius: FormControl<number>;
  innerPadding: FormControl<number>;
}

export interface TooltipFormShape {
  enabled: FormControl<boolean>;
  content: FormControl<string>;
  appearance: FormGroup<TooltipAppearanceFormShape>;
}

export interface PercentFormShape {
  minPrice: FormControl<number>;
  maxPrice: FormControl<number>;
  cartPercentage: FormControl<number>;
}

export interface DigitalUpsellDiscountFormShape {
  enabled: FormControl<boolean>;
  applyTo: FormControl<DigitalUpsellDiscountApplyTo>;
  discountType: FormControl<DiscountType>;
  value: FormControl<number>;
  labelText: FormControl<string>;
  labelColor: FormControl<string>;
}

export interface DigitalUpsellFormShape {
  title: FormControl<string>;
  description: FormControl<string>;
  pricingMethod: FormControl<DigitalUpsellPricingMethod>;
  flatPrice: FormControl<number>;
  percent: FormGroup<PercentFormShape>;
  showInCart: FormControl<boolean>;
  discountConfiguration: FormGroup<DigitalUpsellDiscountFormShape>;
  autoSelectOnlyFreeItem: FormControl<boolean>;
  productImageBase64: FormControl<string>;
  appearance: FormGroup<{
    card: FormGroup<CardAppearanceFormShape>;
    image: FormGroup<ImageAppearanceFormShape>;
  }>;
  tooltip: FormGroup<TooltipFormShape>;
  selectingMethod: FormGroup<SelectingMethodFormShape>;
  timer: FormGroup<TimerFormShape>;
  customCode: FormGroup<CustomCodeFormShape>;
}

export const DIGITAL_UPSELL_VARIABLES: CustomCodeVariableDoc[] = [
  {
    reference: '{{componentId}}',
    type: 'String',
    description: 'Unique widget ID, useful as a DOM hook for your own CSS or JS',
  },
  { reference: '{{title}}', type: 'String', description: 'The offer title' },
  { reference: '{{{description}}}', type: 'HTML', description: 'The offer description (rich text)' },
  { reference: '{{imageUrl}}', type: 'String', description: 'The product image URL' },
  { reference: '{{pricingMethod}}', type: 'String', description: "'fixed' or 'percent'" },
  { reference: '{{selectingMethodType}}', type: 'String', description: "The add control: 'toggle' or 'checkbox'" },
  { reference: '{{showInCart}}', type: 'Boolean', description: 'True when the added offer shows in the cart list' },
  { reference: '{{timerEnabled}}', type: 'Boolean', description: 'True when the countdown timer is enabled' },
  { reference: '{{timerPlacement}}', type: 'String', description: "Where the timer shows: 'inline' or 'badge'" },
  { reference: '{{tooltipEnabled}}', type: 'Boolean', description: 'True when the info tooltip is enabled' },
  { reference: '{{{tooltipContent}}}', type: 'HTML', description: 'The tooltip body (rich text)' },
  {
    reference: 'data-pdq-slot="control"',
    type: 'Slot',
    description: 'The add control (toggle/checkbox) — click handling + Shopify are ours. Required.',
  },
  { reference: 'data-pdq-slot="price"', type: 'Slot', description: 'The live price (re-prices with the cart)' },
  { reference: 'data-pdq-slot="timer"', type: 'Slot', description: 'The countdown timer' },
  {
    reference: 'data-pdq-action="toggle"',
    type: 'Directive',
    description: 'Clicking this element adds/removes the offer — use it to build your own button',
  },
  {
    reference: 'data-pdq-show-if="added"',
    type: 'Directive',
    description: 'Element shows only while the offer is in the cart',
  },
  {
    reference: 'data-pdq-show-if="not-added"',
    type: 'Directive',
    description: 'Element shows only while the offer is not yet added',
  },
];

export const DIGITAL_UPSELL_STARTER_TEMPLATE = `<div
  style="display: flex; align-items: center; gap: 12px; padding: 12px 20px;"
>
  {{#if imageUrl}}
    <img src="{{imageUrl}}" alt="" style="width: 52px; height: 52px; object-fit: contain; border-radius: 8px; flex-shrink: 0;" />
  {{/if}}

  <div style="flex: 1; min-width: 0;">
    <div style="font-size: 14px; font-weight: 600; color: #333;">{{title}}</div>
    <div style="font-size: 12px; color: #666; margin-top: 2px;">{{{description}}}</div>

    <!-- Live countdown (only if enabled in the Timer tab). We render it; you place it. -->
    {{#if timerEnabled}}
      <div data-pdq-slot="timer" style="margin-top: 6px;"></div>
    {{/if}}
  </div>

  <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 8px; flex-shrink: 0;">
    <!-- Live price. We render it; you place it. -->
    <div data-pdq-slot="price" style="font-size: 14px; font-weight: 600; color: #333; white-space: nowrap;"></div>

    <!-- The add control. We handle the click + Shopify. Keep this slot (or build your own
         button with data-pdq-action="toggle"). -->
    <div data-pdq-slot="control"></div>
  </div>
</div>`;

const DEFAULTS = {
  title: '',
  description: '',
  pricingMethod: 'fixed' as DigitalUpsellPricingMethod,
  flatPrice: 0,
  percent: { minPrice: 2, maxPrice: 10, cartPercentage: 10 },
  showInCart: true,
  discountConfiguration: {
    enabled: false,
    applyTo: 'upsellItem' as DigitalUpsellDiscountApplyTo,
    discountType: 'fixedAmount' as DiscountType,
    value: 10,
    labelText: '',
    labelColor: DEFAULT_DISCOUNT_LABEL_COLOR,
  },
  autoSelectOnlyFreeItem: false,
  productImageBase64: '',
  appearance: {
    card: {
      backgroundColor: '#FFFFFF',
      textColor: '#333333',
      borderColor: '#EEEEEE',
      borderWidth: 0,
      cornerRadius: 0,
      innerPadding: 0,
    },
    image: { fit: 'contain' as DigitalUpsellImageFit, cornerRadius: 0 },
  },
  tooltip: {
    enabled: false,
    content: '',
    appearance: {
      backgroundColor: '#FFFFFF',
      borderColor: '#E0E0E0',
      textColor: '#333333',
      borderWidth: 0,
      cornerRadius: 0,
      innerPadding: 8,
    },
  },
  selectingMethod: {
    type: 'toggle' as SelectingMethodType,
    defaultColor: '#EEEEEE',
    selectedColor: '#000000',
  },
};

export function createDigitalUpsellFormGroup(
  initialSettings?: Record<string, unknown> | null,
): FormGroup<DigitalUpsellFormShape> {
  const formGroup = new FormGroup<DigitalUpsellFormShape>({
    title: new FormControl(DEFAULTS.title, { nonNullable: true, validators: [nonBlankValidator()] }),
    description: new FormControl(DEFAULTS.description, { nonNullable: true }),
    pricingMethod: new FormControl(DEFAULTS.pricingMethod, { nonNullable: true }),
    flatPrice: new FormControl(DEFAULTS.flatPrice, { nonNullable: true }),
    percent: new FormGroup<PercentFormShape>({
      minPrice: new FormControl(DEFAULTS.percent.minPrice, { nonNullable: true }),
      maxPrice: new FormControl(DEFAULTS.percent.maxPrice, { nonNullable: true }),
      cartPercentage: new FormControl(DEFAULTS.percent.cartPercentage, { nonNullable: true }),
    }),
    showInCart: new FormControl(DEFAULTS.showInCart, { nonNullable: true }),
    discountConfiguration: new FormGroup<DigitalUpsellDiscountFormShape>({
      enabled: new FormControl(DEFAULTS.discountConfiguration.enabled, { nonNullable: true }),
      applyTo: new FormControl(DEFAULTS.discountConfiguration.applyTo, { nonNullable: true }),
      discountType: new FormControl(DEFAULTS.discountConfiguration.discountType, { nonNullable: true }),
      value: new FormControl(DEFAULTS.discountConfiguration.value, { nonNullable: true }),
      labelText: new FormControl(DEFAULTS.discountConfiguration.labelText, { nonNullable: true }),
      labelColor: new FormControl(DEFAULTS.discountConfiguration.labelColor, { nonNullable: true }),
    }),
    autoSelectOnlyFreeItem: new FormControl(DEFAULTS.autoSelectOnlyFreeItem, { nonNullable: true }),
    productImageBase64: new FormControl(DEFAULTS.productImageBase64, { nonNullable: true }),
    appearance: new FormGroup({
      card: new FormGroup<CardAppearanceFormShape>({
        backgroundColor: new FormControl(DEFAULTS.appearance.card.backgroundColor, { nonNullable: true }),
        textColor: new FormControl(DEFAULTS.appearance.card.textColor, { nonNullable: true }),
        borderColor: new FormControl(DEFAULTS.appearance.card.borderColor, { nonNullable: true }),
        borderWidth: new FormControl(DEFAULTS.appearance.card.borderWidth, { nonNullable: true }),
        cornerRadius: new FormControl(DEFAULTS.appearance.card.cornerRadius, { nonNullable: true }),
        innerPadding: new FormControl(DEFAULTS.appearance.card.innerPadding, { nonNullable: true }),
      }),
      image: new FormGroup<ImageAppearanceFormShape>({
        fit: new FormControl(DEFAULTS.appearance.image.fit, { nonNullable: true }),
        cornerRadius: new FormControl(DEFAULTS.appearance.image.cornerRadius, { nonNullable: true }),
      }),
    }),
    tooltip: new FormGroup<TooltipFormShape>({
      enabled: new FormControl(DEFAULTS.tooltip.enabled, { nonNullable: true }),
      content: new FormControl(DEFAULTS.tooltip.content, { nonNullable: true }),
      appearance: new FormGroup<TooltipAppearanceFormShape>({
        backgroundColor: new FormControl(DEFAULTS.tooltip.appearance.backgroundColor, { nonNullable: true }),
        borderColor: new FormControl(DEFAULTS.tooltip.appearance.borderColor, { nonNullable: true }),
        textColor: new FormControl(DEFAULTS.tooltip.appearance.textColor, { nonNullable: true }),
        borderWidth: new FormControl(DEFAULTS.tooltip.appearance.borderWidth, { nonNullable: true }),
        cornerRadius: new FormControl(DEFAULTS.tooltip.appearance.cornerRadius, { nonNullable: true }),
        innerPadding: new FormControl(DEFAULTS.tooltip.appearance.innerPadding, { nonNullable: true }),
      }),
    }),
    selectingMethod: new FormGroup<SelectingMethodFormShape>({
      type: new FormControl(DEFAULTS.selectingMethod.type, { nonNullable: true }),
      defaultColor: new FormControl(DEFAULTS.selectingMethod.defaultColor, { nonNullable: true }),
      selectedColor: new FormControl(DEFAULTS.selectingMethod.selectedColor, { nonNullable: true }),
    }),
    timer: createTimerFormGroup(),
    customCode: createCustomCodeFormGroup(undefined, DIGITAL_UPSELL_STARTER_TEMPLATE),
  });

  if (initialSettings) {
    formGroup.patchValue(initialSettings, { emitEvent: false });
  }

  return formGroup;
}
